import { Injectable } from '@angular/core';
import {
    BatterySetDto,
    BatterySetService,
    CraftDto,
    CraftService,
    EquipmentDto,
    EquipmentService,
    OrganisationRelationshipDto,
    OrganisationRelationshipService,
    OrganisationSubscriptionDto,
    RpaTypeDto,
    RpaTypesService,
    SubscriptionService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import * as moment from 'moment';
import { ReplaySubject, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Injectable()
export class OrganisationRelationshipsUiService {
    private organisationRpaListSubject = new ReplaySubject<CraftDto[]>(1);
    organisationRpaList$ = this.organisationRpaListSubject.asObservable();

    private organisationRpaTypesListSubject = new ReplaySubject<RpaTypeDto[]>(
        1
    );

    private organisationBatterySetListSubject = new ReplaySubject<
        BatterySetDto[]
    >(1);
    organisationBatterySetList$ =
        this.organisationBatterySetListSubject.asObservable();

    private organisationEquipmentListSubject = new ReplaySubject<
        EquipmentDto[]
    >(1);
    organisationEquipmentList$ =
        this.organisationEquipmentListSubject.asObservable();

    private childrenSubject = new ReplaySubject<OrganisationRelationshipDto[]>(
        1
    );
    children$ = this.childrenSubject.asObservable();

    private subscriptionSubject =
        new ReplaySubject<OrganisationSubscriptionDto>(1);
    subscription$ = this.subscriptionSubject.asObservable();

    private ngUnsubscribe$ = new Subject<void>();
    private organisationId: number;

    constructor(
        private workTracker: WorkTracker,
        private craftService: CraftService,
        private rpaTypesService: RpaTypesService,
        private organisationRelationshipService: OrganisationRelationshipService,
        private subscriptionService: SubscriptionService,
        private batterySetService: BatterySetService,
        private equipmentService: EquipmentService
    ) {
        organisationRelationshipService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshRelationships());
        subscriptionService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshRelationships());
        rpaTypesService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.findOrganisationRpaTypes());
        craftService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshRelationships());
        batterySetService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshRelationships());
        equipmentService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshRelationships());
    }

    setup(organisationId: number) {
        this.organisationId = organisationId;
        this.refreshRelationships();
        this.findOrganisationRpas();
        this.findOrganisationRpaTypes();
        this.findOrganisationBatterySets();
        this.findOrganisationEquipment();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.organisationBatterySetListSubject.complete();
        this.organisationEquipmentListSubject.complete();
        this.organisationRpaListSubject.complete();
        this.organisationRpaTypesListSubject.complete();
    }

    /**
     * Finds whether a given RPA type is global or custom and being shared with a given child organisation.
     * Emits the type that will also need to be shared if it is a custom type not already shared with the child organisation.
     * If either the type is global, or it is already shared, the return value will be null.
     * The component calling this function can use the return value to continuing with only sharing the RPA if the return is null,
     * or by prompting the user and also sharing the RPA type if the return contains that type.
     *
     * @param rpaId The ID of the RPA being shared down.
     * @param childId The child organisation the RPA is being shared to.
     * @returns Returns null if the RPA has a global type or it's custom type is already shared,
     * else returns the RpaTypeDto for the required type.
     */
    findRpaTypeToShare(rpaId: number, childId: number) {
        return combineLatest([
            this.organisationRpaListSubject,
            this.organisationRpaTypesListSubject,
            this.childrenSubject
        ]).pipe(
            map(([rpa, rpaTypes, children]) => {
                const rpaTypeId = rpa.find(r => r.id === rpaId).rpaTypeId;
                // If the organisation has rpaTypes, find the one that matches the RPA's type
                // This will be null both if the organisation has no custom types,
                // or if the RPA type isn't a custom type. Else it will be the RpaTypeDto
                const customRpaTypeToShare =
                    rpaTypes?.length > 0
                        ? rpaTypes
                              .filter(type => type.organisationId != null)
                              .find(type => type.id === rpaTypeId)
                        : null;

                // Find the list of custom RPA types already shared to the child org.
                const sharedTypes = children.find(
                    c => c.id === childId
                )?.sharedRpaTypeList;

                // This logic basically does:
                // 1. If the rpa type is not shared to the child org, return the customRpaTypeToShare value
                // 2. If the RPA type is not global, the customRpaTypeToShare will be a RpaTypeDto
                // 3. If either the type is global, or it is already shared, the return value will be null
                return sharedTypes?.find(t => t.id === rpaTypeId) == null
                    ? customRpaTypeToShare
                    : null;
            }),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    private refreshRelationships() {
        if (this.organisationId == null) {
            return;
        }

        const currentTime = moment(moment.now()).format(
            'YYYY-MM-DD[T]HH:mm:ss'
        );

        combineLatest([
            this.organisationRelationshipService.findChildRelationships(
                this.organisationId
            ),
            this.subscriptionService.findCurrentSubscriptions(
                this.organisationId
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([results, subscriptions]) => {
                this.subscriptionSubject.next(
                    subscriptions.find(
                        s =>
                            s.status ===
                            OrganisationSubscriptionDto.Status.ACTIVE
                    )
                );
                this.childrenSubject.next(
                    results.filter(child => {
                        return child.endTime > currentTime || !child.endTime;
                    })
                );
            })
            .add(this.workTracker.createTracker());
    }

    private findOrganisationRpas() {
        this.craftService
            .findCrafts(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(rpas => {
                this.organisationRpaListSubject.next(rpas);
            });
    }

    private findOrganisationRpaTypes() {
        this.rpaTypesService
            .findRpaTypes(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(rpaTypes => {
                this.organisationRpaTypesListSubject.next(rpaTypes);
            });
    }

    private findOrganisationBatterySets() {
        this.batterySetService
            .findBatterySets(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(batterySetList => {
                this.organisationBatterySetListSubject.next(batterySetList);
            });
    }

    private findOrganisationEquipment() {
        this.equipmentService
            .find(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(equipmentList => {
                this.organisationEquipmentListSubject.next(equipmentList);
            });
    }
}
